import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { EMOJI_GROUPS, emojiName, recentEmoji, rememberEmoji, searchEmoji } from '../lib/emoji';

/** Emoji per row in the picker (the grid's columns, for the arrow keys). */
const COLUMNS = 8;

/**
 * A small emoji picker that opens next to the button that asked for it (a sheet at the bottom of
 * a phone's screen): recently used first, then groups, and a search by name. Arrow keys move
 * through the grid, Enter picks, Escape closes and puts focus back on the button.
 */
export function EmojiPicker({
  anchor,
  title = 'Choose an emoji',
  onPick,
  onClose,
  backTo
}: {
  anchor: HTMLElement | null;
  title?: string;
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Where focus goes when it closes (1.12.31: the message box, to keep typing); the button by default. */
  backTo?: HTMLElement | null;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });
  const recent = useMemo(() => recentEmoji(), []);
  const found = useMemo(() => (query.trim() ? searchEmoji(query) : null), [query]);

  // Next to the button, flipped above it when there's no room below; a sheet on a narrow screen.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (vw < 560 || !anchor) return setStyle({ left: 8, right: 8, bottom: 8 });
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const top = r.bottom + 6 + h <= vh - 8 ? r.bottom + 6 : Math.max(8, r.top - h - 6);
    const left = Math.min(Math.max(8, r.right - w), vw - w - 8);
    setStyle({ top, left });
  }, [anchor]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!box.current?.contains(t) && !anchor?.contains(t)) onClose();
    };
    const gone = () => onClose();
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('resize', gone);
    };
  }, [anchor, onClose]);
  // Focus goes back to the button that opened it (or where the caller asked).
  const returnTo = useRef(backTo);
  returnTo.current = backTo;
  useEffect(() => () => (returnTo.current ?? anchor)?.focus(), [anchor]);

  const pick = (emoji: string) => {
    rememberEmoji(emoji);
    onPick(emoji);
  };
  const cells = () => [...(box.current?.querySelectorAll<HTMLButtonElement>('.emoji-cell') ?? [])];
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return onClose();
    }
    const all = cells();
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const inSearch = (e.target as HTMLElement).tagName === 'INPUT';
    if (inSearch) {
      if (e.key === 'ArrowDown' && all[0]) {
        e.preventDefault();
        all[0].focus();
      } else if (e.key === 'Enter' && found?.[0]) {
        e.preventDefault();
        pick(found[0]);
      }
      return;
    }
    if (at < 0) return;
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: COLUMNS, ArrowUp: -COLUMNS }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    const next = at + step;
    if (next < 0) box.current?.querySelector<HTMLInputElement>('.emoji-search')?.focus();
    else all[Math.min(next, all.length - 1)]?.focus();
  };

  const section = (name: string, list: string[]) => (
    <div key={name} className="emoji-section">
      <h3 className="emoji-section-title">{name}</h3>
      <div className="emoji-grid" role="group" aria-label={name}>
        {list.map((e, i) => (
          <button key={`${e}${i}`} type="button" className="emoji-cell" aria-label={emojiName(e)} title={emojiName(e)} onClick={() => pick(e)}>
            {e}
          </button>
        ))}
      </div>
    </div>
  );

  return createPortal(
    <div ref={box} className="emoji-pop" role="dialog" aria-label={title} style={style} onKeyDown={onKey}>
      <input className="input emoji-search" type="search" placeholder="Search emoji" aria-label="Search emoji" value={query} autoFocus onChange={(e) => setQuery(e.target.value)} />
      <div className="emoji-scroll">
        {found ? (
          found.length ? (
            section('Found', found)
          ) : (
            <p className="emoji-none">No emoji called that.</p>
          )
        ) : (
          <>
            {recent.length > 0 && section('Recently used', recent)}
            {EMOJI_GROUPS.map((g) => section(g.name, g.list.map(([e]) => e)))}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
